import React, { useRef, useEffect, useState } from 'react';
import { 
  Play, 
  Pause, 
  SkipBack, 
  SkipForward, 
  Shuffle, 
  Repeat, 
  Repeat1, 
  Heart,
  Volume2,
  VolumeX 
} from 'lucide-react';
import ExpandedNowPlaying from './ExpandedNowPlaying';

const Player = ({
  currentSong,
  isPlaying,
  setIsPlaying,
  currentTime,
  setCurrentTime,
  duration,
  setDuration,
  volume,
  setVolume,
  isShuffled,
  setIsShuffled,
  repeatMode,
  setRepeatMode,
  nextSong,
  previousSong,
  likedSongs,
  toggleLike,
  playSong,
  user,
  isPlayingAd,
  onAdComplete
}) => {
  const audioRef = useRef(null);
  const progressRef = useRef(null);
  const [showExpandedView, setShowExpandedView] = useState(false);

  useEffect(() => {
    if (audioRef.current && currentSong) {
      // Reset audio when song changes
      audioRef.current.load();
      
      if (isPlaying) {
        // Small delay to ensure audio is loaded
        const playPromise = audioRef.current.play();
        if (playPromise !== undefined) {
          playPromise.catch(console.error);
        }
      }
    }
  }, [currentSong]);

  useEffect(() => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.play().catch(console.error);
      } else {
        audioRef.current.pause();
      }
    }
  }, [isPlaying]);

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = volume;
    }
  }, [volume]);

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
      // Track listening activity
      if (currentSong && currentSong.id) {
        trackListening(currentSong);
      }
    }
  };

  const trackListening = async (song) => {
    const token = localStorage.getItem('auth_token');
    if (!token) return;

    try {
      await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/listening/track?song_id=${song.id}&song_title=${encodeURIComponent(song.title)}&artist=${encodeURIComponent(song.artist)}`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
    } catch (error) {
      console.error('Failed to track listening:', error);
    }
  };

  const handleEnded = () => {
    // If an ad just finished, call the ad complete handler
    if (isPlayingAd) {
      onAdComplete();
      return;
    }
    
    if (repeatMode === 'one') {
      audioRef.current.currentTime = 0;
      audioRef.current.play();
    } else {
      nextSong();
    }
  };

  const togglePlayPause = () => {
    if (isPlaying) {
      audioRef.current?.pause();
      setIsPlaying(false);
    } else {
      audioRef.current?.play();
      setIsPlaying(true);
    }
  };

  const handleSeek = (e) => {
    const rect = progressRef.current.getBoundingClientRect();
    const pos = (e.clientX - rect.left) / rect.width;
    const newTime = pos * duration;
    
    if (audioRef.current) {
      audioRef.current.currentTime = newTime;
      setCurrentTime(newTime);
    }
  };

  const formatTime = (time) => {
    if (!time || isNaN(time)) return '0:00';
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const handleRepeatClick = () => {
    if (repeatMode === 'off') {
      setRepeatMode('all');
    } else if (repeatMode === 'all') {
      setRepeatMode('one');
    } else {
      setRepeatMode('off');
    }
  };

  if (!currentSong) {
    console.log('⚠️ Player: No currentSong, returning placeholder');
    return <div className="player-placeholder"></div>;
  }

  console.log('🎵 Player rendering with song:', currentSong.title, 'audioUrl:', currentSong.audioUrl);

  return (
    <div className="player">
      <audio
        ref={audioRef}
        src={currentSong.audioUrl || '/placeholder-audio.mp3'}
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
        onEnded={handleEnded}
      />
      
      {/* Song Info */}
      <div className="player-song-info">
        <img 
          src={currentSong.albumArt || '/placeholder-album.jpg'} 
          alt={currentSong.title}
          className="player-album-art"
          onClick={() => setShowExpandedView(true)}
          style={{ cursor: 'pointer', opacity: 1 }}
          title="Click to expand"
        />
        <div className="player-text-info">
          {isPlayingAd && (
            <div style={{ 
              color: '#FFD700', 
              fontSize: '9px', 
              fontWeight: '500', 
              marginBottom: '4px',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              opacity: 0.8
            }}>
              📢 Advertisement
            </div>
          )}
          <div className="player-song-title">{currentSong.title}</div>
          <div className="player-artist">{currentSong.artist}</div>
        </div>
        {!isPlayingAd && (
          <button 
            className={`player-like-btn ${likedSongs.has(currentSong.id) ? 'liked' : ''}`}
            onClick={() => toggleLike(currentSong.id)}
          >
            <Heart size={16} fill={likedSongs.has(currentSong.id) ? '#9D4EDD' : 'none'} />
          </button>
        )}
      </div>

      {/* Player Controls */}
      <div className="player-controls">
        <div className="player-control-buttons">
          <button 
            className={`control-btn ${isShuffled ? 'active' : ''}`}
            onClick={() => setIsShuffled(!isShuffled)}
          >
            <Shuffle size={16} />
          </button>
          
          <button 
            className="control-btn" 
            onClick={previousSong}
            disabled={isPlayingAd}
            style={{ opacity: isPlayingAd ? 0.3 : 1, cursor: isPlayingAd ? 'not-allowed' : 'pointer' }}
          >
            <SkipBack size={20} />
          </button>
          
          <button 
            className="control-btn play-btn"
            onClick={() => setIsPlaying(!isPlaying)}
          >
            {isPlaying ? <Pause size={20} /> : <Play size={20} />}
          </button>
          
          <button 
            className="control-btn" 
            onClick={nextSong}
            disabled={isPlayingAd}
            style={{ opacity: isPlayingAd ? 0.3 : 1, cursor: isPlayingAd ? 'not-allowed' : 'pointer' }}
          >
            <SkipForward size={20} />
          </button>
          
          <button 
            className={`control-btn ${repeatMode !== 'off' ? 'active' : ''}`}
            onClick={handleRepeatClick}
          >
            {repeatMode === 'one' ? <Repeat1 size={16} /> : <Repeat size={16} />}
          </button>
        </div>
        
        {/* Progress Bar */}
        <div className="player-progress">
          <span className="time-display">{formatTime(currentTime)}</span>
          <div 
            className="progress-bar"
            ref={progressRef}
            onClick={handleSeek}
          >
            <div className="progress-track"></div>
            <div 
              className="progress-fill"
              style={{ width: `${duration ? (currentTime / duration) * 100 : 0}%` }}
            ></div>
          </div>
          <span className="time-display">{formatTime(duration)}</span>
        </div>
      </div>

      {/* Volume Control */}
      <div className="player-volume">
        <button className="control-btn volume-btn">
          {volume === 0 ? <VolumeX size={16} /> : <Volume2 size={16} />}
        </button>
        <input
          type="range"
          min="0"
          max="1"
          step="0.05"
          value={volume}
          onChange={(e) => setVolume(parseFloat(e.target.value))}
          className="volume-slider"
        />
      </div>

      {/* Expanded Now Playing View */}
      {showExpandedView && (
        <ExpandedNowPlaying
          song={currentSong}
          isPlaying={isPlaying}
          onClose={() => setShowExpandedView(false)}
          onPlayPause={togglePlayPause}
          onPrevious={previousSong}
          onNext={nextSong}
          currentTime={currentTime}
          duration={duration}
          onSeek={(time) => {
            if (audioRef.current) {
              audioRef.current.currentTime = time;
              setCurrentTime(time);
            }
          }}
          volume={volume}
          onVolumeChange={setVolume}
          isLiked={likedSongs.has(currentSong?.id)}
          onToggleLike={toggleLike}
          onAddToPlaylist={(song) => {
            // TODO: Implement add to playlist functionality
            alert('Add to playlist feature coming soon!');
          }}
          onShare={(song) => {
            // Copy share link to clipboard
            const shareUrl = `${window.location.origin}?song=${song.id}`;
            navigator.clipboard.writeText(shareUrl).then(() => {
              alert('Share link copied to clipboard!');
            });
          }}
          isShuffleOn={isShuffled}
          isRepeatOn={repeatMode !== 'off'}
          onToggleShuffle={() => setIsShuffled(!isShuffled)}
          onToggleRepeat={() => {
            const modes = ['off', 'all', 'one'];
            const currentIndex = modes.indexOf(repeatMode);
            const nextMode = modes[(currentIndex + 1) % modes.length];
            setRepeatMode(nextMode);
          }}
          playSong={playSong}
          user={user}
        />
      )}
    </div>
  );
};

export default Player;